import { Card, CardBody, Tab, Tabs } from '@heroui/react';
import React from 'react';
import { FieldErrors } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { FormFooter } from '../../../../components/FormFooter';
import { useNotification } from '../../../../contexts/NotificationContext';
import {
  isIntegrationPayloadLocked,
  useIntegrationSettingsAccess,
} from '../../../../featureFlags';
import { useCreateSupplier } from '../../hooks/useCreateSupplier';
import { useUpdateDeliverySettings } from '../../hooks/useUpdateDeliverySettings';
import { useUpdateSupplier } from '../../hooks/useUpdateSupplier';
import { buildIntegrationPayload } from '../../utils/buildIntegrationPayload';
import {
  buildPostSaveBaseline,
  buildSupplierUpdateInput,
  hasSupplierChanges,
  refusedIntegrationFields,
} from '../../utils/buildSupplierUpdate';
import { hasAnyDeliveryDay } from '../../utils/deliveryDays';
import { CreateFormFields } from '../SupplierCreate/components/CreateFormFields';

import { formatFieldList } from './components';
import { useActiveTab } from './hooks';
import { useSupplierForm, SupplierFormProvider } from './SupplierFormProvider';
import {
  OrderAgentTab,
  OrderPreferencesTab,
  ProfileTab,
  PromotionsTab,
  SettingsTab,
  NotificationsTab,
  CatalogTab,
  DeliveryZonesTab,
  TaxTab,
  TeamTab,
} from './tabs';
import {
  TabType,
  tabLabels,
  type TabProps,
  type SupplierFormData,
} from './types';

/**
 * The fields saved by `updateSupplierDeliverySetting` rather than
 * `supplierUpdate`. They have their own mutation and their own failure, so a
 * rebaseline has to leave them alone when that one did not land.
 */
/**
 * Which tab owns a field, for the fields that can fail validation.
 *
 * Only needed because this form is tabbed: a message about a field on a tab the
 * reader is not looking at tells them nothing. Absent means the message stands on
 * its own without naming a tab.
 */
const FIELD_TABS: Partial<Record<string, TabType>> = {
  name: TabType.PROFILE,
  primaryCategory: TabType.PROFILE,
  deliveryFee: TabType.PROFILE,
  minimumOrderValue: TabType.PROFILE,
  currency: TabType.PROFILE,
  locale: TabType.SETTINGS,
  timezone: TabType.SETTINGS,
  pricingDateSource: TabType.SETTINGS,
  calculationPrecision: TabType.SETTINGS,
  notificationOrderEmails: TabType.NOTIFICATIONS,
  notificationSystemAlertEmails: TabType.NOTIFICATIONS,
  notificationComplaintEmails: TabType.NOTIFICATIONS,
  notificationWhatsappNumber: TabType.NOTIFICATIONS,
  notificationSmsNumber: TabType.NOTIFICATIONS,
};

// Internal component to render content that needs form context
const SupplierFormContent: React.FC<TabProps> = ({ mode, supplier }) => {
  /**
   * The settings an integrated distributor's integration depends on are
   * read-only unless `adminCanEditSupplierIntegrationSettings` is on for the
   * person editing.
   *
   * LaunchDarkly resolves the flags before the first render, but the signed-in
   * email arrives after that, so briefly the flags are the anonymous context's.
   * For a flag targeted at people that answers off, and a save in that window
   * refuses those fields rather than sending them.
   */
  const integrationAccess = useIntegrationSettingsAccess();

  // The promotions and tax tabs read a distributor's pricing configuration, so
  // the same flag that opens the integration settings opens them.
  const gatedTabs = integrationAccess.canChange
    ? []
    : [TabType.PROMOTIONS, TabType.TAX];

  const { activeTab, setActiveTab, availableTabs } = useActiveTab(gatedTabs);
  const {
    formMethods: {
      handleSubmit,
      setError,
      reset,
      getValues,
      setValue,
      formState: { isSubmitting, isDirty, dirtyFields, defaultValues },
    },
  } = useSupplierForm();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const { updateSupplier } = useUpdateSupplier({
    supplierId: supplier?.id ?? '',
  });
  const { updateDeliverySettings } = useUpdateDeliverySettings({
    supplierId: supplier?.id,
  });
  const { createSupplier } = useCreateSupplier();

  const integrationSettingsLocked = isIntegrationPayloadLocked(
    integrationAccess,
    Boolean(supplier?.isIntegrated),
    {
      isPuppy: supplier?.isPuppy,
      isTest: supplier?.isTest,
    }
  );

  const renderTabContent = (tab: TabType) => {
    switch (tab) {
      case TabType.PROFILE:
        return <ProfileTab mode={mode} supplier={supplier} />;
      case TabType.ORDER_PREFERENCES:
        return <OrderPreferencesTab mode={mode} supplier={supplier} />;
      case TabType.CATALOG:
        return <CatalogTab supplier={supplier} />;
      case TabType.DELIVERY_ZONES:
        return <DeliveryZonesTab supplier={supplier} />;
      case TabType.SETTINGS:
        return <SettingsTab mode={mode} supplier={supplier} />;
      case TabType.NOTIFICATIONS:
        return <NotificationsTab mode={mode} supplier={supplier} />;
      case TabType.TEAM:
        return <TeamTab supplier={supplier} />;
      case TabType.PROMOTIONS:
        return <PromotionsTab supplier={supplier} />;
      case TabType.TAX:
        return <TaxTab supplier={supplier} />;
      case TabType.ORDER_AGENT:
        return <OrderAgentTab supplier={supplier} />;
      default:
        // Hidden/unbuilt tabs are never offered; fall back to the profile tab.
        return <ProfileTab mode={mode} supplier={supplier} />;
    }
  };

  const handleValidationErrors = (errors: FieldErrors<SupplierFormData>) => {
    const [field, firstError] = Object.entries(errors)[0] ?? [];
    const message =
      firstError &&
      'message' in firstError &&
      typeof firstError.message === 'string'
        ? firstError.message
        : 'Please fill in all required fields.';

    // Open the tab that owns it, in edit mode. A field the reader cannot see
    // otherwise blocks the save with nothing on screen to explain it, which is what
    // the two refusals below also guard against. Creation is one page, so there is
    // no tab to move to and naming one would send the reader looking for something
    // that is not there.
    const tab = mode === 'edit' && field ? FIELD_TABS[field] : undefined;
    if (tab) setActiveTab(tab);

    addNotification({
      title: 'Validation Error',
      message: tab ? `${message} (${tabLabels[tab]} tab)` : message,
      type: 'error',
    });
  };

  /**
   * Convert the sentinel 'NO_CATALOG' value (used in the form dropdown) to
   * the backend representation: { defaultSubCatalog: '', noDefaultCatalog: true }.
   * Any other value passes through as-is with noDefaultCatalog: false.
   *
   * Backend contract (verified): updateSupplierDeliverySetting.cutoffTimes is a
   * full-map PutItem replace. Similarly supplierUpdate.noDefaultCatalog + defaultSubCatalog
   * are the canonical pair for "hide all products".
   */
  const getCatalogPayload = (
    rawValue: string
  ): { defaultSubCatalog: string; noDefaultCatalog: boolean } => {
    if (rawValue === 'NO_CATALOG') {
      return { defaultSubCatalog: '', noDefaultCatalog: true };
    }
    return {
      defaultSubCatalog: rawValue || '',
      noDefaultCatalog: false,
    };
  };

  const handleSave = handleSubmit(async (formValues: SupplierFormData) => {
    // Deep-copied before anything is awaited. react-hook-form hands the handler
    // its live values object, so an edit made while a save is in flight mutates
    // it, and a shallow copy still shares the nested ones: delivery days, cutoff
    // times and the category list are all Records or arrays. The rebaseline
    // would then treat that newer input as already saved and throw it away.
    const data = structuredClone(formValues);

    if (mode === 'create' || !supplier?.id) {
      let newSupplierId: string;
      try {
        const newSupplier = await createSupplier(data);
        newSupplierId = newSupplier.id;
        addNotification({
          title: 'Created',
          message: 'Distributor created successfully',
          type: 'success',
        });
      } catch {
        addNotification({
          title: 'Error',
          message: 'Failed to create distributor. Please try again.',
          type: 'error',
        });
        return;
      }

      // Delivery settings for the freshly created distributor, sent only when a
      // delivery day was actually chosen. The id exists only once
      // `createSupplier` returns, so it is passed per call. Mirrors legacy
      // `useSupplierCreate`, which chained `updateSupplierDeliverySetting` after
      // `supplierCreate`.
      //
      // The create page does not offer these controls, and in edit mode the
      // modal owns the mutation, so this is the one save path left on this form.
      // It stays for a create that does carry a schedule; today none does, and
      // the guard skips it.
      if (hasAnyDeliveryDay(data.deliveryDays)) {
        try {
          await updateDeliverySettings(data, newSupplierId);
        } catch (deliveryError) {
          addNotification({
            title: 'Delivery settings not saved',
            message:
              deliveryError instanceof Error && deliveryError.message
                ? deliveryError.message
                : 'The distributor was created, but its delivery settings did not save. Set them on the Order Preferences tab.',
            type: 'error',
          });
        }
      }

      navigate(`/suppliers/${newSupplierId}`);
      return;
    }

    // Emptying the minimum order looks like removing it and is not. The payload
    // carries no minOrder, supplierUpdate merges, and the old minimum stays live
    // with nothing on screen to say so. Nothing in the mutation deletes a stored
    // minimum, so the save is refused and the reader is told what does work.
    if (dirtyFields.minimumOrderValue && !data.minimumOrderValue?.trim()) {
      setError('minimumOrderValue', {
        message: 'Enter 0 to let buyers order any amount.',
      });
      setActiveTab(TabType.PROFILE);
      addNotification({
        title: 'Minimum order not removed',
        message: 'Enter 0 on the Profile tab to let buyers order any amount.',
        type: 'error',
      });
      return;
    }

    // Emptying the decimal places cannot put the currency default back: the
    // backend drops a null exactly like an absent key, so the stored number
    // would stay in force with nothing on screen to say so. Refuse the save and
    // say what is possible. The tooltip on the field warns before this happens.
    if (
      dirtyFields.calculationPrecision &&
      data.calculationPrecision === undefined
    ) {
      setError('calculationPrecision', {
        message:
          'This cannot go back to the currency default. Enter a number, or press Cancel to leave it as it was.',
      });
      setActiveTab(TabType.SETTINGS);
      addNotification({
        title: 'Decimal places not cleared',
        message:
          'Once set, this can only be changed to another number. Press Cancel to leave it as it was.',
        type: 'error',
      });
      return;
    }

    // --- P0-3: include minOrderStrict in the min-order payload ---
    const minOrderAmountCents =
      data.minimumOrderValue && data.minimumOrderValue !== ''
        ? Math.round(parseFloat(data.minimumOrderValue) * 100)
        : undefined;

    // Build minOrder, always including strict so the value is never silently lost.
    let minOrderPayload:
      | { price?: { amount: number; currency: string }; strict?: boolean }
      | undefined;
    if (minOrderAmountCents !== undefined) {
      minOrderPayload = {
        price: {
          amount: minOrderAmountCents,
          currency: data.minimumOrderCurrency ?? 'EUR',
        },
        strict: data.minOrderStrict,
      };
    } else if (dirtyFields.minOrderStrict) {
      // Even if price is absent, preserve the strict flag change when touched
      minOrderPayload = { strict: data.minOrderStrict };
    }

    // --- P0-2: convert NO_CATALOG sentinel to backend fields ---
    const { defaultSubCatalog, noDefaultCatalog } = getCatalogPayload(
      data.supplierDefaultSubCatalog
    );

    const integrationPayload = buildIntegrationPayload(
      data,
      supplier?.integration,
      dirtyFields
    );

    // Only what the person editing actually changed. The form holds every field
    // across six tabs, and submitting all of them rewrote untouched values as a
    // side effect of any edit.
    const updateInput = buildSupplierUpdateInput({
      data,
      dirtyFields,
      loadedIsTest: supplier?.isTest,
      integrationPayload,
      minOrderPayload,
      catalogPayload: { defaultSubCatalog, noDefaultCatalog },
      integrationSettingsLocked,
    });

    // This handler does not touch the delivery schedule. `DeliverySettingsModal`
    // owns that mutation, because `cutoffTimes` is persisted as a full-map
    // replace: while the schedule lived on this form, one profile save rewrote
    // all of it and could put back days somebody else had just changed.
    if (hasSupplierChanges(updateInput, dirtyFields)) {
      try {
        await updateSupplier(updateInput);
      } catch {
        addNotification({
          title: 'Error',
          message: 'Failed to save distributor. Please try again.',
          type: 'error',
        });
        return;
      }
    }

    // Rebaseline on what was just saved. Edited fields are what a save sends,
    // and react-hook-form keeps a field edited until the form is reset: without
    // this, a later save of one field resends every field touched since the tab
    // was opened, overwriting whatever a colleague changed in between. That is
    // the same overwriting this PR exists to stop, one save later.
    //
    // Anything typed while the save was in flight is newer than what was sent,
    // so it is written back afterwards and left unsaved.
    const current = getValues();
    const stillUnsaved = (
      Object.keys(current) as (keyof SupplierFormData)[]
    ).filter((field) => {
      const now = current[field];
      // By value, not identity: the snapshot is a deep copy, so a nested value
      // is never the same object and a reference check would call every one of
      // them edited and leave it dirty after every save.
      if (typeof now === 'object' && now !== null) {
        return JSON.stringify(now) !== JSON.stringify(data[field]);
      }

      return now !== data[field];
    });

    // Fields the guard refused. They never reached the backend, so the form must
    // not keep showing them. The usual cause is access changing between the edit
    // and the save, since LaunchDarkly pushes that live.
    const refused = refusedIntegrationFields(
      dirtyFields,
      integrationSettingsLocked
    );

    // Refused fields are baselined to what the form loaded, because `reset` makes
    // whatever it is given the new default: baseline a field to the value that was
    // just rejected and the `setValue` below cannot mark it dirty, leaving Save
    // disabled on a value no backend holds.
    reset(buildPostSaveBaseline(data, defaultValues, refused));

    stillUnsaved.forEach((field) => {
      setValue(field, current[field], { shouldDirty: true });
    });

    if (refused.length > 0) {
      const isOne = refused.length === 1;
      addNotification({
        title: 'Some changes were not saved',
        // Only claims the rest saved when the rest actually saved.
        message: `${formatFieldList(refused)} ${isOne ? 'is' : 'are'} read-only for you, so ${
          isOne ? 'it has' : 'they have'
        } been put back. Everything else saved.`,
        type: 'warning',
      });
    }
  }, handleValidationErrors);

  const handleCancel = () => {
    navigate('/suppliers');
  };

  return (
    <>
      <Card id="supplier-form">
        {/* Creation is one page, not seven tabs. Four fields are needed to create
            a distributor and they sat on two different tabs, behind four that
            cannot do anything until it exists, so the required ones were the
            hardest things on the screen to find. Editing keeps the tabs: by then
            every tab works and the form is a reference rather than a form to
            complete. */}
        {mode === 'create' ? (
          <CardBody className="p-0">
            <CreateFormFields />
          </CardBody>
        ) : (
          <CardBody className="p-4">
            <Tabs
              aria-label="Distributor sections"
              selectedKey={activeTab}
              onSelectionChange={(key) => setActiveTab(key as TabType)}
              variant="underlined"
              classNames={{
                tabList: 'gap-6 w-full relative px-6 border-b border-divider',
                cursor: 'w-full',
                tab: 'max-w-fit px-2 h-12',
                tabContent: 'group-data-[selected=true]:text-primary',
              }}
            >
              {availableTabs.map((tab) => (
                <Tab key={tab} title={tabLabels[tab]}>
                  {renderTabContent(tab)}
                </Tab>
              ))}
            </Tabs>
          </CardBody>
        )}
      </Card>
      <FormFooter
        saveText={mode === 'create' ? 'Create Distributor' : 'Save Changes'}
        onSave={handleSave}
        onCancel={handleCancel}
        isSaving={isSubmitting}
        isSaveDisabled={!isDirty || isSubmitting}
        isCancelDisabled={isSubmitting}
      />
    </>
  );
};

export const SupplierForm: React.FC<TabProps> = ({ mode, supplier }) => {
  return (
    <SupplierFormProvider mode={mode} supplier={supplier}>
      <SupplierFormContent mode={mode} supplier={supplier} />
    </SupplierFormProvider>
  );
};
